import { useAuth } from 'components/AuthContext';
import {AccessToken, constructAccessToken, isTokenValid} from 'hooks/auth/Token';

export type Fetcher = (url: string, options: RequestInit) => Promise<Response>;
export type FetchHook = () => Fetcher;

export class UnauthenticatedError extends Error {}

let refreshPromise: Promise<AccessToken> | null = null;

const fetchNewToken = async (token: AccessToken, url: string, fetcher: Fetcher): Promise<AccessToken> => {
    const headers = new Headers();
    headers.set('Authorization', `Bearer ${token.refreshToken}`);
    const response = await fetcher(url, {method: 'POST', headers,});

    if (!response.ok) {
        throw new UnauthenticatedError();
    }

    const data = await response.json();

    return constructAccessToken(data.accessToken as string, token.refreshToken);
};

export const refreshAccessToken = (token: AccessToken, refreshTokenUrl: string, fetcher: Fetcher): Promise<AccessToken> => {
    refreshPromise ??= fetchNewToken(token, refreshTokenUrl, fetcher
    ).finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
};


export const createAuthFetch = (refreshTokenUrl: string, innerFetch: Fetcher) => {
    return function useAuthFetch() {
        const {token, setToken, handleUnauthenticated} = useAuth();

        return async (url: string, options: RequestInit = {}): Promise<Response> => {
            if (!token) {
                handleUnauthenticated();
                throw new UnauthenticatedError();
            }

            let tokenToUse = token;

            if (!isTokenValid(tokenToUse)) {
                try {
                    tokenToUse = await refreshAccessToken(tokenToUse, refreshTokenUrl, innerFetch);

                    setToken(tokenToUse);
                } catch {
                    handleUnauthenticated();
                    throw new UnauthenticatedError();
                }
            }

            const headers = new Headers(options.headers);

            headers.set('Authorization', `Bearer ${tokenToUse.accessToken}`);

            return innerFetch(url, {...options, headers});
        };
    };
};

export const withApiHeaders = (fetch: Fetcher) => {
    const withHeadersFetch = async (url: string, options: RequestInit = {}) => {
        const headers = new Headers(options.headers);
        headers.set('Content-Type', 'application/json');
        headers.set('Accept', 'application/json');
        return fetch(url, { ...options, headers });
    }

    return withHeadersFetch;
}

export const useUnauthenticatedHandling = (fetch: Fetcher) => {
    const { handleUnauthenticated } = useAuth();
    const withUnauthenticatedHandlingFetch = async (url: string, options: RequestInit = {}) => {
        const response = await fetch(url, options);

        if (response.status === 401) {
            handleUnauthenticated();
            return Promise.reject(new UnauthenticatedError());
        }

        return response;
    }

    return withUnauthenticatedHandlingFetch;
}

export const useRegularFetch = () => {
    return fetch;
}